import { Attention, CheckOne, CloseOne, Info } from '@icon-park/react';
import { type MemoExoticComponent, memo, type ReactElement, type ReactNode } from 'react';

import { COLOR_GROUPS } from '../../../config/colors/constants';
import { Card } from '../../data-display/card/Card';
import type { CARD_VARIANTS } from '../../data-display/card/constants';
import { HtmlFormatter } from '../../data-display/html-formatter/HtmlFormatter';
import { IconShadow } from '../../general/icon-shadow/IconShadow';
import { Typography } from '../../general/typography/Typography';
import * as styles from './AlertBanner.styles';
import { ALERT_BANNER_VARIANTS } from './constants';

const ALERT_BANNER_ICON_MAPPING: Record<ALERT_BANNER_VARIANTS, ReactElement> = {
  [ALERT_BANNER_VARIANTS.INFO]: <Info />,
  [ALERT_BANNER_VARIANTS.DANGER]: <CloseOne />,
  [ALERT_BANNER_VARIANTS.WARNING]: <Attention />,
  [ALERT_BANNER_VARIANTS.SUCCESS]: <CheckOne />,
};

const ALERT_BANNER_ICON_COLOR_GROUP_MAPPING: Record<ALERT_BANNER_VARIANTS, COLOR_GROUPS> = {
  [ALERT_BANNER_VARIANTS.INFO]: COLOR_GROUPS.PRIMARY,
  [ALERT_BANNER_VARIANTS.DANGER]: COLOR_GROUPS.DANGER,
  [ALERT_BANNER_VARIANTS.WARNING]: COLOR_GROUPS.WARNING,
  [ALERT_BANNER_VARIANTS.SUCCESS]: COLOR_GROUPS.SUCCESS,
};

const ALERT_BANNER_CARD_VARIANTS_MAPPING: Record<ALERT_BANNER_VARIANTS, CARD_VARIANTS> = {
  [ALERT_BANNER_VARIANTS.INFO]: Card.VARIANTS.PRIMARY,
  [ALERT_BANNER_VARIANTS.DANGER]: Card.VARIANTS.DANGER,
  [ALERT_BANNER_VARIANTS.WARNING]: Card.VARIANTS.WARNING,
  [ALERT_BANNER_VARIANTS.SUCCESS]: Card.VARIANTS.SUCCESS,
};

export type AlertBannerProps = {
  /** Type of alert banner. */
  variant?: ALERT_BANNER_VARIANTS;
  /** Override default icon. */
  icon?: ReactElement;
  /** Text title of the banner. */
  title?: ReactNode;
  /** Content of the banner. */
  children?: ReactNode;
};

/**
 * Card-based banner with a title, a content and a variant icon, used for info, success,
 * warning or danger messages.
 *
 * @param props.variant - Banner variant, driving the card color and the default icon.
 * @param props.icon - Icon overriding the variant icon.
 * @param props.title - Title displayed above the content.
 * @param props.children - Content of the banner.
 * @returns The alert banner.
 */
export const AlertBannerBase = ({
  variant = ALERT_BANNER_VARIANTS.INFO,
  icon = null,
  title = null,
  children = null,
}: AlertBannerProps): JSX.Element => (
  <Card variant={ALERT_BANNER_CARD_VARIANTS_MAPPING[variant]}>
    <div css={styles.container}>
      <HtmlFormatter css={styles.text}>
        {title && <Typography variant={Typography.VARIANTS.HEADING_4}>{title}</Typography>}

        {children}
      </HtmlFormatter>

      <IconShadow
        icon={icon || ALERT_BANNER_ICON_MAPPING[variant]}
        colorGroup={ALERT_BANNER_ICON_COLOR_GROUP_MAPPING[variant]}
        hasCircleBorder={!icon}
      />
    </div>
  </Card>
);

AlertBannerBase.displayName = 'AlertBanner';

export const AlertBanner: MemoExoticComponent<typeof AlertBannerBase> & {
  VARIANTS?: typeof ALERT_BANNER_VARIANTS;
} = memo(AlertBannerBase);

AlertBanner.VARIANTS = ALERT_BANNER_VARIANTS;
